import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

export default function Terms() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-smoke-50 via-flame-50 to-smoke-100">
      <div className="max-w-4xl mx-auto px-4 py-8 sm:px-6 lg:px-8">
        <Link 
          to="/" 
          className="inline-flex items-center gap-2 text-flame-600 hover:text-flame-700 mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Calculator
        </Link>

        <div className="bg-white rounded-lg shadow-lg p-8">
          <h1 className="text-3xl font-bold text-smoke-900 mb-6">Terms of Service</h1>
          
          <div className="prose prose-sm max-w-none space-y-6 text-smoke-700">
            <p className="text-sm text-smoke-500">
              <strong>Effective Date:</strong> February 7, 2026
            </p>

            <section>
              <h2 className="text-xl font-semibold text-smoke-900 mt-8 mb-4">Agreement to Terms</h2>
              <p>
                By accessing or using BBQLabs.ai ("Service"), a product of Escape Velocity Ventures ("Company," 
                "we," "our," or "us"), you agree to be bound by these Terms of Service. If you do not agree 
                with any part of these terms, you may not use our Service.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-smoke-900 mt-8 mb-4">Description of Service</h2>
              <p>
                BBQLabs.ai is a web-based calculator that provides estimated cook times for barbecue and smoking 
                meats. The Service uses algorithmic calculations based on:
              </p>
              <ul className="list-disc pl-6 space-y-2 my-4">
                <li>Smoker specifications and thermal properties</li>
                <li>Meat types, weights, and target temperatures</li>
                <li>Weather conditions and environmental factors</li>
                <li>Cooking techniques (wrapping, spritzing, resting)</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-smoke-900 mt-8 mb-4">⚠️ Critical Food Safety Disclaimer</h2>
              <div className="bg-yellow-50 border-2 border-yellow-400 rounded-lg p-4 my-4">
                <p className="font-semibold text-yellow-900">
                  THE CALCULATIONS PROVIDED ARE ESTIMATES ONLY AND NOT PROFESSIONAL ADVICE.
                </p>
                <ul className="list-disc pl-6 space-y-2 mt-3 text-yellow-900">
                  <li>Cook times vary significantly based on your specific equipment, environment, and meat</li>
                  <li><strong>ALWAYS use a calibrated meat thermometer to verify internal temperature</strong></li>
                  <li>Never rely solely on time estimates for food safety</li>
                  <li>Follow USDA/FDA guidelines for safe cooking temperatures</li>
                  <li>Improperly cooked meat may cause serious illness or death</li>
                  <li>We are not liable for any foodborne illness resulting from use of this Service</li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-smoke-900 mt-8 mb-4">No Warranties - Use at Your Own Risk</h2>
              <p>
                THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER 
                EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO:
              </p>
              <ul className="list-disc pl-6 space-y-2 my-4">
                <li>Accuracy, reliability, or completeness of calculations</li>
                <li>Fitness for a particular purpose</li>
                <li>Non-infringement</li>
                <li>Freedom from errors or interruptions</li>
              </ul>
              <p className="mt-4">
                We make no guarantee that the Service will meet your requirements or that cook time estimates 
                will be accurate for your specific situation.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-smoke-900 mt-8 mb-4">Limitation of Liability</h2>
              <p>
                TO THE MAXIMUM EXTENT PERMITTED BY LAW:
              </p>
              <ul className="list-disc pl-6 space-y-2 my-4">
                <li>We are NOT liable for any foodborne illness, injury, death, or property damage resulting 
                    from use of this Service</li>
                <li>We are NOT liable for any direct, indirect, incidental, consequential, or punitive damages</li>
                <li>We are NOT liable for any loss of profits, data, use, goodwill, or other intangible losses</li>
                <li>You assume full responsibility for food safety when using our estimates</li>
              </ul>
              <p className="mt-4 font-semibold">
                IF YOU ARE NOT COMFORTABLE WITH THESE TERMS, DO NOT USE THIS SERVICE.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-smoke-900 mt-8 mb-4">User Responsibilities</h2>
              <p>You agree to:</p>
              <ul className="list-disc pl-6 space-y-2 my-4">
                <li>Use the Service as informational guidance only, not as a substitute for proper food safety training</li>
                <li>Follow all applicable food safety regulations and best practices</li>
                <li>Use appropriate food thermometers and monitoring equipment</li>
                <li>Not abuse, disrupt, or attempt to gain unauthorized access to the Service</li>
                <li>Not use the Service for any illegal or unauthorized purpose</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-smoke-900 mt-8 mb-4">No Data Storage</h2>
              <p>
                We do not collect, store, or retain any personal data. All calculations are performed locally 
                in your browser. You are responsible for any data you choose to save locally (e.g., PDF downloads, 
                browser caches).
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-smoke-900 mt-8 mb-4">Third-Party Weather Services</h2>
              <p>
                Weather data is provided by third-party APIs (National Weather Service, Open-Meteo). We are 
                not responsible for the accuracy, availability, or policy changes of these services.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-smoke-900 mt-8 mb-4">Intellectual Property</h2>
              <p>
                The Service, including its algorithms, design, and content, is owned by Escape Velocity Ventures 
                and protected by copyright, trademark, and other intellectual property laws. You may not:
              </p>
              <ul className="list-disc pl-6 space-y-2 my-4">
                <li>Copy, modify, or reverse-engineer the Service</li>
                <li>Remove copyright or proprietary notices</li>
                <li>Use our trademarks without written permission</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-smoke-900 mt-8 mb-4">Modifications to Service and Terms</h2>
              <p>
                We reserve the right to:
              </p>
              <ul className="list-disc pl-6 space-y-2 my-4">
                <li>Modify or discontinue the Service at any time without notice</li>
                <li>Update these Terms at any time (changes effective upon posting)</li>
                <li>Refuse service to anyone for any reason</li>
              </ul>
              <p className="mt-4">
                Your continued use of the Service after changes constitutes acceptance of the new Terms.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-smoke-900 mt-8 mb-4">Governing Law</h2>
              <p>
                These Terms are governed by the laws of the State of [Your State], without regard to conflict 
                of law principles. Any disputes shall be resolved in the courts of [Your State/County].
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-smoke-900 mt-8 mb-4">Severability</h2>
              <p>
                If any provision of these Terms is found to be unenforceable, the remaining provisions will 
                remain in full force and effect.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-smoke-900 mt-8 mb-4">Contact Information</h2>
              <p>
                For questions about these Terms, contact:
              </p>
              <p className="mt-2">
                <strong>Escape Velocity Ventures</strong><br />
                Email: legal@escapevelo.com
              </p>
            </section>

            <div className="border-t border-smoke-200 mt-8 pt-6">
              <p className="text-sm text-smoke-600 font-semibold">
                BY USING BBQLABS.AI, YOU ACKNOWLEDGE THAT YOU HAVE READ, UNDERSTOOD, AND AGREE TO BE BOUND BY 
                THESE TERMS OF SERVICE.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
